// The town's facts on the one condition language (phase D joined: @rpg/story's `Conditions`, the seams' item 2): the
// town registers `time.hours` and `npc.<id>.place` there, and story's quests, ink, barks and the rpg layer's shops read
// them beside their own. A source is registered by name with a reader over the fact's dotted path; what it knows is
// its listed keys, so a content check knows them before any play.
import { Conditions, type FactValue } from "@rpg/story";

/** A source of the town's facts under one name: `read(["hours"])` for `time.hours`, `read(["smith", "place"])` for
 *  `npc.smith.place`; undefined when it has no such fact now. */
export interface TownFactSource {
  name: string;
  read(path: readonly string[]): FactValue | undefined;
  /** Every key it answers (`hours`, `smith.place`). */
  keys(): string[];
}

/** The town's view of the shared conditions: registers its sources there and reads any fact by its dotted key. */
export class TownFacts {
  constructor(readonly conditions: Conditions = new Conditions()) {}
  register(src: TownFactSource): void {
    if (this.conditions.source(src.name)) throw new Error(`facts: "${src.name}" is registered twice`);
    this.conditions.register({ name: src.name, get: (k) => src.read(k.split(".")) ?? null, knows: (k) => src.keys().includes(k), keys: () => src.keys() });
  }
  /** A fact by its dotted key (`npc.smith.place`), or undefined when no source knows it. */
  get(key: string): FactValue | undefined {
    return this.conditions.knows(key) ? this.conditions.fact(key) : undefined;
  }
  /** Every fact the town's sources answer now, with its value. */
  all(): Record<string, FactValue> {
    const out: Record<string, FactValue> = {};
    for (const name of ["time", "npc"]) {
      const s = this.conditions.source(name);
      for (const k of s?.keys?.() ?? []) out[`${name}.${k}`] = this.get(`${name}.${k}`) ?? null;
    }
    return out;
  }
}

/** The keys the town answers, offline (a content check): `time.hours` and each townsperson's `npc.<id>.place`. */
export function townFactKeys(npcIds: readonly string[]): { time: string[]; npc: string[] } {
  return { time: ["hours"], npc: npcIds.map((id) => `${id}.place`) };
}
